import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { GISMap } from "@/components/GISMap";
import { TwinMap } from "@/components/TwinMap";
import { Layers, Globe2 } from "lucide-react";

export const DigitalTwin = () => {
  const [enabled, setEnabled] = useState({ parcels: true, mining: true, protected: true });

  const layers = useQuery({ queryKey: ["twin-layers"], queryFn: async () => (await api.get("/twin/layers")).data });
  const parcels = useQuery({
    queryKey: ["twin-parcels"],
    queryFn: async () => (await api.get("/public/map/parcels")).data,
    enabled: !!enabled.parcels,
  });
  const mining = useQuery({
    queryKey: ["twin-mining"],
    queryFn: async () => (await api.get("/public/map/mineral-rights")).data,
    enabled: !!enabled.mining,
  });
  const protectedQ = useQuery({
    queryKey: ["twin-protected"],
    queryFn: async () => (await api.get("/spatial/protected-layers")).data,
    enabled: !!enabled.protected,
  });

  // Convert protected areas into "parcel" style for the map component
  const protectedFeatures = (enabled.protected ? (protectedQ.data || []) : []).map((p) => ({
    id: p.id, parcel_number: p.name, coordinates: p.polygon,
    province: p.province, district: p.kind.replace(/_/g, " "),
    land_type: p.kind, area_ha: 0,
  }));

  const parcelFeatures = [
    ...(enabled.parcels ? (parcels.data || []) : []),
    ...protectedFeatures,
  ];

  return (
    <div className="space-y-6" data-testid="page-twin">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">National Spatial Digital Twin</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Globe2 className="text-[#C99A2E]"/> Unified National Map
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Cadastral, environment, infrastructure, planning and geodetic reference layers on a single interactive canvas.</div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <aside className="cad-panel p-4">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5] flex items-center gap-1.5 mb-3">
            <Layers size={12}/> Layer catalogue
          </div>
          <div className="space-y-2">
            {(layers.data || []).map((l) => (
              <label key={l.id} className="flex items-start gap-2 text-[12px] cursor-pointer" data-testid={`twin-layer-${l.id}`}>
                <input type="checkbox"
                       checked={!!enabled[l.id]}
                       onChange={(e) => setEnabled({ ...enabled, [l.id]: e.target.checked })}
                       className="mt-0.5 accent-[#C99A2E]" />
                <div>
                  <div className="text-[#F6F8FA]">{l.name}</div>
                  <div className="text-[10px] font-mono-cad text-[#7C93A5] uppercase tracking-widest">{l.kind} · {l.source} · {l.features}</div>
                </div>
              </label>
            ))}
          </div>
        </aside>
        <div className="lg:col-span-3">
          <TwinMap
            parcels={parcelFeatures}
            minerals={enabled.mining ? (mining.data || []) : []}
            height={620}
          />
        </div>
      </div>
    </div>
  );
};
